import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, MapPin, Video, RefreshCw, PenSquare, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { GOOGLE_FLASH } from '../../lib/communications';
import InteractionLogger from '../../components/crm/InteractionLogger';
import AccountPicker from '../../components/admin/AccountPicker';
import Modal from '../../components/admin/Modal';
import EmptyState from '../../components/crm/EmptyState';

// /admin/calendar — your own Google Calendar, read live, with each guest
// matched to a CRM contact so a meeting becomes a logged interaction in one
// click. Read-only: nothing is stored, nothing is written back to Google.

const NY = 'America/New_York';
const ymd = (d) => d.toLocaleDateString('en-CA', { timeZone: NY });
const shiftYmd = (s, days) => { const d = new Date(`${s}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
const dayLabel = (s) => new Date(`${s}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: NY });
// "2026-09-29 14:00:00" in New York, the shape the interactions API stores.
const sqliteLocal = (iso) => {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: NY, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(new Date(iso));
  const g = (t) => p.find((x) => x.type === t)?.value;
  return `${g('year')}-${g('month')}-${g('day')} ${g('hour') === '24' ? '00' : g('hour')}:${g('minute')}:${g('second')}`;
};

const FLASH = { ...GOOGLE_FLASH, connected: { tone: 'lime', text: 'Google connected. Your calendar is below.' } };
const toneCls = { lime: 'bg-[#B0FC38]/10 border-[#B0FC38]/25 text-[#B0FC38]', red: 'bg-red-500/10 border-red-500/20 text-red-300', amber: 'bg-amber-500/10 border-amber-500/20 text-amber-200', grey: 'bg-white/[0.04] border-white/[0.08] text-gray-300' };

export default function StaffCalendar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(null);
  const [logTarget, setLogTarget] = useState(null); // { event, accountId?, contactId?, contactName? }
  const [connecting, setConnecting] = useState(false);
  const today = ymd(new Date());
  const from = searchParams.get('from') || shiftYmd(today, -7);
  const to = searchParams.get('to') || shiftYmd(today, 14);

  const google = searchParams.get('google');
  useEffect(() => {
    if (!google) return;
    setFlash(FLASH[google] || { tone: 'grey', text: `Google sign-in finished with status “${google}”.` });
    const sp = new URLSearchParams(searchParams); sp.delete('google'); setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await api.get(`/api/admin/calendar/events?from=${from}&to=${to}`)); }
    catch (err) { setError(err?.message || 'Could not load your calendar'); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const move = (days) => { const sp = new URLSearchParams(searchParams); sp.set('from', shiftYmd(from, days)); sp.set('to', shiftYmd(to, days)); setSearchParams(sp); };
  const reset = () => { const sp = new URLSearchParams(searchParams); sp.delete('from'); sp.delete('to'); setSearchParams(sp); };

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/api/auth/google/start?scope=gmail&return_to=/admin/calendar');
      if (res?.authUrl) window.location.assign(res.authUrl);
    } catch (err) { setError(err?.message || 'Could not start Google sign-in'); setConnecting(false); }
  };

  const byDay = useMemo(() => {
    const m = new Map();
    for (const e of data?.events || []) { if (!m.has(e.day)) m.set(e.day, []); m.get(e.day).push(e); }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const initialFor = (t) => t && ({
    channel: t.event.meetLink ? 'video_call' : t.event.location ? 'in_person' : 'other',
    direction: 'outbound',
    occurredAt: t.event.allDay ? `${t.event.day} 09:00:00` : sqliteLocal(t.event.start),
    subject: t.event.title,
    contactId: t.contactId || undefined,
  });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Calendar</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Your Google Calendar, read live. Guests who are school contacts are named, so a meeting can be logged as an interaction in one click.</p>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] text-gray-300 border border-white/[0.1] hover:text-white" title="Reload from Google">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
        </button>
      </div>

      {flash && <div role="status" className={`mb-4 px-3 py-2 rounded-lg text-[12px] border ${toneCls[flash.tone] || toneCls.grey}`}>{flash.text}</div>}
      {error && <div role="alert" className="mb-4 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}

      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="calendar-range">
        <button type="button" onClick={() => move(-7)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05]" aria-label="Earlier week"><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-[13px] text-gray-200">{dayLabel(from)} → {dayLabel(to)}</span>
        <button type="button" onClick={() => move(7)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05]" aria-label="Later week"><ChevronRight className="w-4 h-4" /></button>
        {(searchParams.get('from') || searchParams.get('to')) && <button type="button" onClick={reset} className="text-[12px] text-[#B0FC38] hover:underline">Back to this week</button>}
      </div>

      {loading && !data ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-white/[0.03] animate-pulse" />)}</div>
      ) : data && !data.connected ? (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-center" data-testid="calendar-not-connected">
          <CalendarDays className="w-6 h-6 text-gray-500 mx-auto mb-2" aria-hidden="true" />
          <div className="text-[14px] text-white font-medium">{data.reason === 'not_configured' ? 'Google sign-in isn’t configured on this environment.' : 'Connect your Google account to see your calendar here.'}</div>
          <p className="text-[12px] text-gray-500 mt-1 max-w-md mx-auto">The same sign-in that connects your Gmail for Communications also grants calendar reading. Nothing is written to your calendar.</p>
          {data.reason !== 'not_configured' && <button type="button" onClick={connect} disabled={connecting} className="mt-4 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50" data-testid="calendar-connect">{connecting ? 'Redirecting…' : 'Sign in with Google'}</button>}
        </div>
      ) : data && data.reason === 'insufficient_scope' ? (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] p-5" role="note" data-testid="calendar-no-scope">
          <div className="text-[13px] text-amber-100 font-medium">Your Google account didn’t grant calendar access.</div>
          <p className="text-[12px] text-amber-100/80 mt-1">Reconnect and leave “See and edit events on your calendars” ticked on Google’s consent screen. Gmail keeps working either way.</p>
          <button type="button" onClick={connect} disabled={connecting} className="mt-3 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50" data-testid="calendar-reconnect">{connecting ? 'Redirecting…' : 'Reconnect with Google'}</button>
        </div>
      ) : data && data.reason ? (
        <div role="alert" className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">Google didn’t answer ({data.reason}{data.status ? ` ${data.status}` : ''}). Try Refresh in a moment.</div>
      ) : byDay.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Nothing on your calendar in this window" description="Use the arrows to look earlier or later." />
      ) : (
        <div className="space-y-5">
          {byDay.map(([day, events]) => (
            <section key={day} data-testid={`calendar-day-${day}`}>
              <h2 className={`text-[11px] uppercase tracking-wider mb-2 ${day === today ? 'text-[#B0FC38]' : day < today ? 'text-gray-600' : 'text-gray-500'}`}>{day === today ? 'Today · ' : ''}{dayLabel(day)}</h2>
              <ul className="space-y-2 list-none m-0 p-0">
                {events.map((e) => <EventRow key={e.id} e={e} past={day < today} onLog={(target) => setLogTarget({ event: e, ...target })} />)}
              </ul>
            </section>
          ))}
        </div>
      )}

      {logTarget && !logTarget.accountId && (
        <Modal open onClose={() => setLogTarget(null)} title={`Which school was “${logTarget.event.title}” about?`} maxWidth="max-w-md">
          <p className="text-[12px] text-gray-500 mb-3">None of the guests is a contact on file. Pick the school and the meeting is logged there.</p>
          <AccountPicker value="" onChange={(id) => id && setLogTarget((t) => ({ ...t, accountId: id }))} placeholder="Search schools…" />
        </Modal>
      )}
      {logTarget?.accountId && (
        <InteractionLogger
          open
          accountId={logTarget.accountId}
          title={`Log “${logTarget.event.title}”`}
          initial={initialFor(logTarget)}
          onClose={() => setLogTarget(null)}
          onLogged={() => { setLogTarget(null); setFlash({ tone: 'lime', text: 'Interaction logged.' }); }}
        />
      )}
    </div>
  );
}

function EventRow({ e, past, onLog }) {
  const guests = e.attendees.filter((a) => !a.self);
  return (
    <li className={`rounded-xl border px-4 py-3 ${past ? 'border-white/[0.04] bg-white/[0.01]' : 'border-white/[0.06] bg-white/[0.02]'} ${e.declined ? 'opacity-50' : ''}`} data-testid={`event-${e.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="text-[12px] text-gray-400 font-mono w-[132px] shrink-0 pt-0.5">{e.allDay ? 'All day' : `${timeLabel(e.start)} – ${timeLabel(e.end)}`}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] text-white font-medium">{e.title}</span>
            {e.declined && <span className="text-[10px] uppercase tracking-wider text-gray-500">declined</span>}
            {e.htmlLink && <a href={e.htmlLink} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white" aria-label="Open in Google Calendar"><ExternalLink className="w-3 h-3" /></a>}
          </div>
          <div className="text-[11.5px] text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
            {e.meetLink && <a href={e.meetLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white"><Video className="w-3 h-3" aria-hidden="true" /> Join video call</a>}
            {e.location && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" aria-hidden="true" /> {e.location}</span>}
          </div>
          {guests.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap text-[11.5px]">
              <Users className="w-3 h-3 text-gray-600 shrink-0" aria-hidden="true" />
              {guests.map((a) => a.match ? (
                <Link key={a.email} to={`/admin/accounts/${a.match.accountId}?tab=contacts`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#B0FC38]/10 border border-[#B0FC38]/25 text-[#B0FC38] hover:bg-[#B0FC38]/20" title={a.email} data-testid={`guest-${a.email}`}>
                  {a.match.contactName || a.email} <span className="opacity-70">· {a.match.accountName}</span>
                </Link>
              ) : (
                <span key={a.email} className="px-2 py-0.5 rounded-full border border-white/[0.08] text-gray-400" title={a.email} data-testid={`guest-${a.email}`}>{a.name || a.email}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
          {e.schools.length ? e.schools.map((s) => (
            <button key={s.accountId} type="button" onClick={() => onLog({ accountId: s.accountId, contactId: s.contactId, contactName: s.contactName })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#B0FC38]/15 border border-[#B0FC38]/40 text-[12px] text-white hover:bg-[#B0FC38]/25" data-testid={`log-${e.id}-${s.accountId}`}>
              <PenSquare className="w-3 h-3" aria-hidden="true" /> Log{e.schools.length > 1 ? ` · ${s.accountName}` : ' meeting'}
            </button>
          )) : (
            <button type="button" onClick={() => onLog({})} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/[0.1] text-[12px] text-gray-400 hover:text-white" data-testid={`log-${e.id}`}>
              <PenSquare className="w-3 h-3" aria-hidden="true" /> Log meeting
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
